import {Component, Vue, Watch} from 'vue-property-decorator';
import {ProductVariant, ProductVariantIncludes, OtherColor, OtherSize} from '@/models/ProductVariant';
import {Contact as ContactModel} from '@/models/Contact';
import {Order} from '@/models/Order';
import {OrderItem} from '@/models/OrderItem';
import {ColorsMap} from '@/views/Webshop/ProductDetail/colors';
import Sidepanel from '@/views/Webshop/Sidepanel/Sidepanel';
import debounce from 'debounce';
import {UserFile} from '@/models/UserFile';
import {AxiosError} from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import ProductSlider from '@/components/product-slider/ProductSlider';
import {Size} from '@/models/Size';
import {Color} from '@/models/Color';
import {Attribute} from '@/models/Attribute';

@Component<ProductDetail>({
  components: {
    Sidepanel,
    ProductSlider,
  },
})
export default class ProductDetail extends Vue {
  public $pageTitle = 'Product detail';
  protected colorsMap = ColorsMap;
  protected product: ProductVariant = new ProductVariant();
  protected contact: ContactModel = new ContactModel();
  protected contactAnswers: any[] = [];
  protected contactImages: any[] = [];
  protected orderItems: OrderItem[] = [];
  protected orderUserId: string = '';
  protected colorOptions: OtherColor[] = [];
  protected sizeOptions: OtherSize[] = [];
  protected selectedSize: string = '';
  public commentDebounce: Function = this.handleComment();
  protected productComment: string = '';
  protected selectedApprovedStatus: boolean = false;
  protected isLoading: boolean = false;
  protected hasAddedToCartInThisView: boolean = false;
  protected approvedOptions: approvedOption[] = [{
    label: 'Ok',
    value: true,
  }, {
    label: 'Niet ok',
    value: false,
  }]
  protected sizeMap: String[] = ['XXS', 'XS', 'S', 'S/M', 'M', 'M/L', 'L', 'XL', 'XXL'];

  public mounted() {
    this.load();
  }

  /**
   * Reload when the route swaps one product for another.
   *
   * vue-router reuses this component when only the params change, so mounted()
   * never runs a second time: the URL changed and the page went on showing the
   * previous product. The shop's own size switch never hit it because
   * setProductVariant() navigates with window.location.href, which is a full
   * page load — but Maia's product cards use $router.push, so opening one
   * product from another landed straight on the bug.
   */
  @Watch('$route.params.productId')
  protected productChanged(next: string, previous: string) {
    if (!next || next === previous) {
      return;
    }

    // Anything this view tracks about the product on screen, rather than about
    // the order, belongs to the product we are leaving.
    this.hasAddedToCartInThisView = false;
    this.productComment = '';
    this.selectedApprovedStatus = false;

    this.load();
  }

  protected load() {
    this.getProduct();
    this.listOrderItems();
    this.getOrder();
  }

  protected getProduct() {
    if (!this.$route.params.productId) {
      return;
    }

    this.isLoading = true;
    new ProductVariant()
    .include([
      ProductVariantIncludes.IMAGES,
      ProductVariantIncludes.MATERIALS,
      ProductVariantIncludes.RESERVED_STOCK,
      ProductVariantIncludes.AVAILABLE_SIZES,
      ProductVariantIncludes.CATEGORIES,
      ProductVariantIncludes.ATTRIBUTES_COUNT,
      ProductVariantIncludes.OTHER_COLORS, // From related variants
      ProductVariantIncludes.OTHER_SIZES, // From related variants
      ProductVariantIncludes.COLOR,
      ProductVariantIncludes.BRAND,
      ProductVariantIncludes.SUPPLIER,
      ProductVariantIncludes.SIZE,
      ProductVariantIncludes.SEASON,
      ProductVariantIncludes.TAGS,
    ])
    .find(this.$route.params.productId)
    .then((product: ProductVariant) => {
      this.product = product;

      if (product.size) {
        this.selectedSize = (product.size as string);
      }

      this.getRelatedFilterOptions();

      if (product.is_approved !== undefined) {
        this.selectedApprovedStatus = product.is_approved;
      }
      if (product.comment !== undefined) {
        this.productComment = product.comment;
      }

      this.isLoading = false;
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected getOrder() {
    new Order()
    .include('user')
    .find(this.$route.params.id)
    .then((order: any) => {
      if (order.status === 'completed') {
        this.$router.push(`order-overview`);
      }
      this.orderUserId = order.user.id;
      this.getContact();
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    })
  }

  protected getContact(){
    new ContactModel()
    .include(['profile', 'answers'])
    .find(this.orderUserId)
    .then((contact: ContactModel) => {
      this.contact = contact;
      this.$root.$emit('breadcrumbUpdated',
      {
        crumb: [
          { name: 'Orders', path: `/orders` },
          { name: `${this.contact.name}` },
        ],
      }
      );

      if(!this.contact.profile || !this.contact.profile.answers) {
        return;
      }

      this.contact.profile.answers = JSON.parse((this.contact.profile as any).answers);

      this.contact.answers = [...(this.contact.profile as any).answers];
      this.contactAnswers = [...(this.contact.profile as any).answers];
      this.$store.dispatch('updateLeadAnswers', this.contact);
      this.fetchFiles();
    });
  }

  protected listOrderItems() {
    new OrderItem()
    .order(this.$route.params.id)
    .include('product')
    .limit(100)
    .all()
    .then((response: any) => {
      this.orderItems = response;
    })
    .catch((error: AxiosError) => {
      console.log('error', error);
    });
  }

  saveProductInfo(productId: string) {
    const payload = {
      comment: this.productComment,
      is_approved: this.selectedApprovedStatus,
    }

    new ProductVariant({id: productId})
    .update(payload)
    .then((response: any) => {
      this.getProduct();
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected fetchCurrentProduct() {
    if (!this.$route.params.productId) {
      return;
    }

    this.isLoading = true;
    new ProductVariant()
    .include([
      ProductVariantIncludes.IMAGES,
      ProductVariantIncludes.RESERVED_STOCK,
      ProductVariantIncludes.OTHER_COLORS,
      ProductVariantIncludes.OTHER_SIZES,
      ProductVariantIncludes.COLOR,
      ProductVariantIncludes.BRAND,
      ProductVariantIncludes.SUPPLIER,
      ProductVariantIncludes.SIZE,
    ])
    .find(this.$route.params.productId)
    .then((product: ProductVariant) => {
      this.product = product;
      this.getRelatedFilterOptions();
      this.isLoading = false;
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    });
  }

  protected createOrderItem(productId: string) {
    const orderItemPayload = {
      product: productId,
    }
    new OrderItem()
    .order(this.$route.params.id)
    .create(orderItemPayload)
    .then((response: any) => {
      this.listOrderItems();
      this.fetchCurrentProduct();
    })
    .catch((error: AxiosError) => {
      if (error.response && error.response.status === 422 && error.response.data && error.response.data.errors && error.response.data.errors.message && error.response.data.errors.message.orders) {
        const orders = (error.response.data.errors.message.orders as string[]).map((order: any) => {
          return `<a target="_blank" href="/webshop/order/${order.id}">${order.name}<br></a>(${order.id}<br>`;
        }).join('<br>');
        ErrorHandler.alert(error.response.data.errors.message.text + '<br><br>' + orders);
      } else if (error.response && error.response.data && error.response.data.errors && error.response.data.errors.message) {
        ErrorHandler.alert(error.response.data.errors.message);
      } else if (error.response && error.response.status === 422 && error.response.data && error.response.data.errors && error.response.data.errors.product) {
        ErrorHandler.alert(error.response.data.errors.product);
      }
    });
  }

  protected setProductVariant(product_variant_id: string) {
    if (product_variant_id) {
      window.location.href = `/webshop/order/${this.$route.params.id}/product/${product_variant_id}`;
    }
  }

  protected getRelatedFilterOptions() {
    if (!this.product) {
      return;
    }

    // Related Colors
    if (this.product.other_colors) {
      this.product.other_colors.forEach((color: OtherColor) => {
        // Exclude current
        if (this.product.color && !this.colorOptions.some((color: OtherColor) => { return color.color_id === (this.product.color as Color).id; })){
          this.colorOptions.push(color);
        }
      })
    }

    // Related sizes
    if (this.product.other_sizes) {
      this.product.other_sizes.forEach((size: OtherSize) => {
        // Exclude current
        if(this.product.size &&
          !this.sizeOptions.some((size: OtherSize) => { return size.size_id === (this.product.size as Size).id})
        ) {
          this.sizeOptions.push(size);
        }
      })
    }

    this.sortSizes();
  }

  protected sortSizes() {
    if(!this.sizeOptions[0]) {
      return;
    }

    if (this.hasNumber(this.sizeOptions[0].name)) {
      this.sizeOptions = this.sizeOptions.sort(this.sortByNumber);
      return;
    }

    this.sizeOptions = this.sizeOptions.sort(this.sortByLetter);
  }

  public handleComment() {
    return debounce(() => {
      this.saveProductInfo((this.product as any).id);
    }, 400);
  }

  protected checkDuplicateOrderItems(productId: string) {
    let duplicate = false;

    this.orderItems.forEach((item) => {
      if (item.product && productId === item.product.id) {
        duplicate = true;
      }
    })
    return duplicate;
  }

  protected sortByNumber(a: OtherSize, b: OtherSize) {
    let x = parseInt(a.name);
    let y = parseInt(b.name);
    return x - y;
  }

  protected sortByLetter(a: OtherSize, b: OtherSize) {
    let x = this.sizeMap.indexOf(a.name);
    let y = this.sizeMap.indexOf(b.name);
    return ((x < y) ? -1 : (x > y) ? 1 : 0);
  }

  protected hasNumber(string: string) {
    return /\d/.test(string);
  }

  protected fetchFiles() {
    new UserFile()
    .user(this.contact)
    .limit(100)
    .all()
    .then((images: any) => {
      this.contactImages = images;
    })
    .catch((error: AxiosError) => {
      ErrorHandler.network(error);
    })
  }

  protected orderItemUpdated() {
    this.listOrderItems();
    this.fetchCurrentProduct();
  }

  @Watch('selectedSize')
  protected changeSize(size: OtherSize) {
    this.setProductVariant(size.product_variant_id);
  }

  protected goToShop() {
    this.$router.push(`/webshop/order/${this.$route.params.id}`);
  }

  protected getValue(attribute: Attribute):string {
    // If the attribute is a select, value contains the option id
    if (! attribute.options) {
      return attribute.value ? attribute.value.toString() : '';
    }

    for (const option of attribute.options) {
      if (option.id === attribute.value)   {
        return option.title ? option.title : '';
      }
    }

    return attribute.value ? attribute.value.toString() : '';
  }

  protected get sidePanelOrderItems(): OrderItem[] {
    return this.orderItems;
  }
}

interface approvedOption {
  label: string;
  value: boolean;
}
